import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import BreadCrumb from '../ass_components/BreadCrumb'
import Meta from '../ass_components/Meta'
import { AiOutlineHome, AiOutlineMail, AiOutlineClockCircle, AiOutlineCopy, AiOutlineCheckCircle, AiOutlineSearch } from "react-icons/ai";
import { translate } from '../i18n';
import { FREE_TRIAL_MONTHS, FREE_TRIAL_PARCELS, SUPPORT_EMAIL } from '../config/onboarding';
import { FAQ, answerOf, fill, matchFaqs } from '../ass_components/chatbot/faq';
import { isContactApiConfigured, sendContactMessage } from '../servics/contactApi';
import { EMPTY_MESSAGE, MESSAGE_MAX, MESSAGE_MIN, TOPICS, mailLinksOf, validate } from './contact/contactMessage';

const VALUES = { email: SUPPORT_EMAIL, months: FREE_TRIAL_MONTHS, parcels: FREE_TRIAL_PARCELS };

/** Ordre des champs : le premier en erreur reçoit le focus. */
const FIELDS = ['name', 'email', 'phone', 'topic', 'message', 'consent'];

/** Réponses de la FAQ (textes partagés avec le chatbot) et leurs liens vers les pages concernées. */
const FaqAnswer = ({ entry, faqTexts }) => (
  <>
    {answerOf(entry, faqTexts, VALUES).map((paragraph) => (
      <p key={paragraph} className='mb-2'>{paragraph}</p>
    ))}
    {entry.actions.length > 0 && (
      <div className='d-flex flex-wrap gap-10 mt-2'>
        {entry.actions.map((action) => (
          <Link key={action.to} className='contact-chip' to={action.to}>{faqTexts.links[action.label]}</Link>
        ))}
      </div>
    )}
  </>
)

/** Envoi depuis la boîte mail de l'utilisateur : application installée, Gmail ou Outlook. */
const MailboxLinks = ({ links, texts }) => (
  <div className='d-flex flex-wrap gap-10'>
    <a className='contact-chip' href={links.app}>{texts.mailbox.app}</a>
    <a className='contact-chip' href={links.gmail} target='_blank' rel='noopener noreferrer'>
      {texts.mailbox.gmail} <span className='visually-hidden'>{texts.mailbox.new_tab}</span>
    </a>
    <a className='contact-chip' href={links.outlook} target='_blank' rel='noopener noreferrer'>
      {texts.mailbox.outlook} <span className='visually-hidden'>{texts.mailbox.new_tab}</span>
    </a>
  </div>
)

/**
 * Page contact : questions fréquentes consultables, formulaire (envoi par l'API de contact
 * si elle est configurée, sinon depuis la boîte mail) et réponses suggérées pendant la saisie,
 * pour que l'utilisateur renonce à envoyer s'il a déjà sa réponse.
 */
const Contact = () => {
  const language = useSelector((state) => state.lang?.lang) || 'FR';
  const texts = translate('contact', language) || translate('contact', 'FR');
  const faqTexts = translate('chatbot', language) || translate('chatbot', 'FR');

  const [values, setValues] = useState(EMPTY_MESSAGE);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  // idle | sending | sent | error | limited (trop d'envois) | mailbox (finaliser depuis la boîte mail) | resolved (réponse trouvée)
  const [status, setStatus] = useState('idle');
  const [sentTo, setSentTo] = useState(null);
  const [dismissed, setDismissed] = useState([]);
  const [search, setSearch] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 2500);
    return () => clearTimeout(timer);
  }, [copied]);

  const faqEntries = search.trim() ? matchFaqs(search, language) : FAQ;
  const suggestions = useMemo(
    () => matchFaqs(values.message, language, 2).filter((entry) => !dismissed.includes(entry.id)),
    [values.message, language, dismissed],
  );
  const links = mailLinksOf(SUPPORT_EMAIL, values, texts);

  const change = (event) => {
    const { name, type, checked, value } = event.target;
    const next = { ...values, [name]: type === 'checkbox' ? checked : value };
    setValues(next);
    if (submitted) setErrors(validate(next));
    if (status === 'mailbox' || status === 'error' || status === 'limited') setStatus('idle');
  };

  const restart = () => {
    setValues(EMPTY_MESSAGE);
    setErrors({});
    setSubmitted(false);
    setDismissed([]);
    setStatus('idle');
  };

  const submit = async (event) => {
    event.preventDefault();
    const found = validate(values);
    setErrors(found);
    setSubmitted(true);
    const firstInvalid = FIELDS.find((field) => found[field]);
    if (firstInvalid) {
      document.getElementById(`contact-${firstInvalid}`)?.focus();
      return;
    }
    // Champ invisible rempli : envoi automatisé, ignoré sans le signaler.
    if (values.website) {
      setSentTo({ name: values.name.trim(), email: values.email.trim() });
      setStatus('sent');
      return;
    }
    if (!isContactApiConfigured()) {
      setStatus('mailbox');
      return;
    }
    setStatus('sending');
    try {
      const { name, email, phone, topic, message } = values;
      await sendContactMessage({
        name: name.trim(), email: email.trim(), phone: phone.trim(), topic, message: message.trim(), language,
      });
      setSentTo({ name: name.trim(), email: email.trim() });
      setValues(EMPTY_MESSAGE);
      setSubmitted(false);
      setStatus('sent');
    } catch (error) {
      setStatus(error.response?.status === 429 ? 'limited' : 'error');
    }
  };

  const copyAddress = () => {
    navigator.clipboard?.writeText(SUPPORT_EMAIL).then(() => setCopied(true), () => {});
  };

  const fieldProps = (name) => ({
    id: `contact-${name}`,
    name,
    onChange: change,
    'aria-invalid': errors[name] ? 'true' : 'false',
    'aria-describedby': errors[name] ? `contact-${name}-error` : undefined,
  });

  const errorOf = (name) => errors[name] && (
    <div id={`contact-${name}-error`} className='contact-error'>
      {fill(texts.errors[errors[name]], { min: MESSAGE_MIN })}
    </div>
  );

  const label = (name, required = true) => (
    <label htmlFor={`contact-${name}`} className='contact-label'>
      {texts.form[name]}{' '}
      {required ? <span aria-hidden='true'>*</span> : <span className='contact-optional'>{texts.form.optional}</span>}
    </label>
  );

  const renderForm = () => (
    <form className='d-flex flex-column gap-15' onSubmit={submit} noValidate>
      <p className='contact-hint mb-0'>{texts.form.required_hint}</p>
      {submitted && Object.keys(errors).length > 0 && (
        <div className='contact-alert contact-alert--error' role='alert'>{texts.errors.summary}</div>
      )}

      <div className='contact-row'>
        <div>
          {label('name')}
          <input type='text' className='form-control' autoComplete='name' maxLength={100} aria-required='true'
                 value={values.name} {...fieldProps('name')} />
          {errorOf('name')}
        </div>
        <div>
          {label('email')}
          <input type='email' className='form-control' autoComplete='email' inputMode='email' maxLength={254}
                 aria-required='true' value={values.email} {...fieldProps('email')} />
          {errorOf('email')}
        </div>
      </div>

      <div className='contact-row'>
        <div>
          {label('phone', false)}
          <input type='tel' className='form-control' autoComplete='tel' inputMode='tel' maxLength={20}
                 placeholder='+228 …' value={values.phone} {...fieldProps('phone')} />
          {errorOf('phone')}
        </div>
        <div>
          {label('topic')}
          <select className='form-select' aria-required='true' value={values.topic} {...fieldProps('topic')}>
            <option value=''>{texts.form.topic_placeholder}</option>
            {TOPICS.map((topic) => <option key={topic} value={topic}>{texts.topics[topic]}</option>)}
          </select>
          {errorOf('topic')}
        </div>
      </div>

      <div>
        {label('message')}
        <textarea rows='6' className='w-100 form-control' maxLength={MESSAGE_MAX} aria-required='true'
                  placeholder={texts.form.message_placeholder} value={values.message} {...fieldProps('message')} />
        <div className='d-flex justify-content-between gap-10'>
          <div>{errorOf('message')}</div>
          <small className='contact-hint text-nowrap'>
            {fill(texts.form.characters, { count: values.message.length, max: MESSAGE_MAX })}
          </small>
        </div>
      </div>

      {suggestions.length > 0 && (
        <section className='contact-suggestions' aria-live='polite' aria-labelledby='contact-suggestions-title'>
          <h4 id='contact-suggestions-title' className='mb-1'>{texts.suggestions.title}</h4>
          <p className='mb-2'>{texts.suggestions.intro}</p>
          {suggestions.map((entry) => (
            <details key={entry.id} className='contact-faq-item' open>
              <summary>{faqTexts.faq[entry.id].question}</summary>
              <div className='contact-faq-answer'><FaqAnswer entry={entry} faqTexts={faqTexts} /></div>
            </details>
          ))}
          <div className='d-flex flex-wrap gap-10 mt-3'>
            <button type='button' className='button border-0' onClick={() => { restart(); setStatus('resolved'); }}>
              {texts.suggestions.resolved}
            </button>
            <button type='button' className='contact-button-secondary'
                    onClick={() => setDismissed([...dismissed, ...suggestions.map((entry) => entry.id)])}>
              {texts.suggestions.not_resolved}
            </button>
          </div>
        </section>
      )}

      <div className='form-check'>
        <input type='checkbox' className='form-check-input' aria-required='true' checked={values.consent}
               {...fieldProps('consent')} />
        <label htmlFor='contact-consent' className='form-check-label'>
          {texts.form.consent} <span aria-hidden='true'>*</span>
        </label>
        {errorOf('consent')}
      </div>

      {/* Piège à robots : invisible pour les visiteurs, ignoré par les lecteurs d'écran. */}
      <div className='contact-honeypot' aria-hidden='true'>
        <label htmlFor='contact-website'>{texts.form.honeypot}</label>
        <input type='text' id='contact-website' name='website' tabIndex={-1} autoComplete='off'
               value={values.website} onChange={change} />
      </div>

      {(status === 'error' || status === 'limited') && (
        <div className='contact-alert contact-alert--error' role='alert'>
          <strong>{status === 'limited' ? texts.status.limited_title : texts.status.error_title}</strong>
          <p className='mb-2'>{status === 'limited' ? texts.status.limited_text : texts.status.error_text}</p>
          <MailboxLinks links={links} texts={texts} />
        </div>
      )}
      {status === 'mailbox' && (
        <div className='contact-alert contact-alert--info' role='status'>
          <strong>{texts.status.mailbox_title}</strong>
          <p className='mb-2'>{texts.status.mailbox_text}</p>
          <MailboxLinks links={links} texts={texts} />
        </div>
      )}

      <div>
        <button type='submit' className='button border-0' disabled={status === 'sending'}>
          {status === 'sending' && texts.form.sending}
          {status === 'error' && texts.status.retry}
          {status !== 'sending' && status !== 'error' && (isContactApiConfigured() ? texts.form.submit : texts.form.prepare)}
        </button>
      </div>
    </form>
  );

  const renderOutcome = () => {
    const sent = status === 'sent';
    return (
      <div className='contact-outcome' role='status'>
        <AiOutlineCheckCircle className='contact-outcome-icon' aria-hidden='true' />
        <h4>{sent ? texts.status.sent_title : texts.suggestions.resolved_title}</h4>
        <p>{sent ? fill(texts.status.sent_text, sentTo) : texts.suggestions.resolved_text}</p>
        <button type='button' className='button border-0' onClick={restart}>{texts.form.reset}</button>
      </div>
    );
  };

  return (
    <>
    <Meta title={texts.meta_title}/>
    <BreadCrumb title={texts.breadcrumb}/>
    <div className='contact-wrapper assiganto-home-wrapper-2 py-5'>
      <div className='container-xxl'>
        <div className='row g-4'>
          <div className='col-12'>
            <h1 className='contact-heading'>{texts.title}</h1>
            <p className='contact-intro mb-0'>{texts.intro}</p>
          </div>

          <div className='col-12'>
            <section className='contact-card' aria-labelledby='contact-faq-title'>
              <div className='d-flex flex-wrap justify-content-between align-items-center gap-15 mb-3'>
                <h2 id='contact-faq-title' className='contact-title mb-0'>{texts.faq.title}</h2>
                <div className='contact-search'>
                  <AiOutlineSearch aria-hidden='true' />
                  <input type='search' className='form-control' value={search} onChange={(e) => setSearch(e.target.value)}
                         placeholder={texts.faq.search_placeholder} aria-label={texts.faq.search_label} />
                </div>
              </div>
              {faqEntries.length === 0 ? (
                <p className='mb-0'>
                  {texts.faq.no_result}{' '}
                  <button type='button' className='contact-link-button' onClick={() => setSearch('')}>{texts.faq.show_all}</button>
                </p>
              ) : (
                <div className='contact-faq'>
                  {faqEntries.map((entry) => (
                    <details key={entry.id} className='contact-faq-item'>
                      <summary>{faqTexts.faq[entry.id].question}</summary>
                      <div className='contact-faq-answer'><FaqAnswer entry={entry} faqTexts={faqTexts} /></div>
                    </details>
                  ))}
                </div>
              )}
            </section>
          </div>

          <div className='col-lg-7'>
            <section className='contact-card h-100' aria-labelledby='contact-form-title'>
              <h2 id='contact-form-title' className='contact-title mb-3'>{texts.form.title}</h2>
              {status === 'sent' || status === 'resolved' ? renderOutcome() : renderForm()}
            </section>
          </div>

          <div className='col-lg-5 d-flex flex-column gap-30'>
            <section className='contact-card' aria-labelledby='contact-mailbox-title'>
              <h2 id='contact-mailbox-title' className='contact-title mb-2'>{texts.mailbox.title}</h2>
              <p className='contact-muted'>{texts.mailbox.text}</p>
              <div className='d-flex flex-wrap align-items-center gap-10 mb-3'>
                <a className='contact-email' href={links.app}>{SUPPORT_EMAIL}</a>
                <button type='button' className='contact-link-button d-inline-flex align-items-center gap-5' onClick={copyAddress}>
                  {copied ? <AiOutlineCheckCircle aria-hidden='true' /> : <AiOutlineCopy aria-hidden='true' />}
                  <span aria-live='polite'>{copied ? texts.mailbox.copied : texts.mailbox.copy}</span>
                </button>
              </div>
              <MailboxLinks links={links} texts={texts} />
            </section>

            <section className='contact-card' aria-labelledby='contact-info-title'>
              <h2 id='contact-info-title' className='contact-title mb-3'>{texts.info.title}</h2>
              <ul className='ps-0 mb-3 contact-info'>
                <li className='mb-3 d-flex align-items-center gap-15'>
                  <AiOutlineHome className='fs-5' aria-label={texts.info.address_label} />
                  <address className='mb-0'>
                  Assiganto Rue Enie,<br/>
                      Adidogomé, Lomé.
                  </address>
                </li>
                {/* Téléphone masqué pour l'instant : réimporter AiOutlinePhone pour le réafficher. */}
                {/*<li className='mb-3 d-flex align-items-center gap-15'>*/}
                {/*  <AiOutlinePhone className='fs-5' />*/}
                {/*  <a href="tel:+228 987 87 87 87">+49 1573 026 98 93</a>*/}
                {/*</li>*/}
                <li className='mb-3 d-flex align-items-center gap-15'>
                  <AiOutlineMail className='fs-5' aria-label={texts.info.email_label} />
                  <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
                </li>
                <li className='mb-3 d-flex align-items-center gap-15'>
                  <AiOutlineClockCircle className='fs-5' aria-label={texts.info.hours_label} />
                  <p className='mb-0'>{texts.info.hours}</p>
                </li>
              </ul>
              <p className='contact-muted mb-0'>{texts.info.response}</p>
            </section>
          </div>

          <div className='col-12'>
            <iframe
            title={texts.map_title}
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3966.860125824984!2d1.227437874612771!3d6.149480193837586!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1023e1f51653f02b%3A0xcc8cea5c0a97cfc6!2sColombe%20de%20la%20Paix!5e0!3m2!1sfr!2sfr!4v1686064421082!5m2!1sfr!2sfr"
            width="600"
            height="400"
            className="border-0 w-100 contact-map"
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade">
            </iframe>
          </div>
        </div>
      </div>
    </div>
    </>
  )
}

export default Contact
